import { useState, type ReactNode } from "react";
import {
  CONSENT_INFO, DISMISS_LABEL, MOVE_STEP_TWO_LINE, MOVE_TO_MIDEYE_CONSENT_BODY, SIGN_IN_APP_NOT_KNOWN_STEP, canChangeAppId,
  consentStatus, mideyeAppLeftover, moveView, ownAppCopy, ownAppRegisterLine, signInAppView, signInChanging, step1Note,
  step1Parts, stepCopy, stepTwoValue, stepsCopy, withoutStepNumber,
} from "../lib/eam";
import type { Rich } from "../lib/eam";
import type { EamEntra, EamEntraSource, EamReadAccess, EamRealm, EamSignInApp, EamUser } from "../types/api";
import CopyButton from "./CopyButton";
import EamRegisterPanel from "./EamRegisterPanel";
import InfoTip from "./InfoTip";
import ModeInfoTip from "./ModeInfoTip";
import OwnAppSteps from "./OwnAppSteps";
import ReadAccessStatus from "./ReadAccessStatus";
import RichText from "./RichText";

const PRIMARY_BUTTON =
  "inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
const CARD = "bg-white rounded-xl shadow-card border border-gray-100 p-6";
const LINK = "text-sm font-medium text-accent hover:text-accent-dark";
const CONSENT_INFO_LABEL = "What approving Mideye in your tenant allows";

interface Props {
  realm: EamRealm;
  entra: EamEntra;
  consentUrl: string | null;
  /** The note after a move ended (Finish, Cancel or a changed number source). */
  note: string | null;
  onDismissNote: () => void;
  busy: boolean;
  onSaveOwnApp: (appId: string) => Promise<string | null>;
  /** "Change the ID": opens the sign-in app panel on the own-app target. */
  onChangeId: () => void;
  /** The listed users; null until the list has loaded. */
  users: readonly EamUser[] | null;
  onSaveMethodId: (methodId: string) => Promise<{ ok: boolean; fieldError: string | null }>;
  /** What Mideye's app holds in the tenant; null while the check runs. */
  readAccess: EamReadAccess | null;
  /** The Entra source the admin is moving to, if any. */
  pendingSource: EamEntraSource | null;
  /** The open sign-in app panel's target before Save; null when it is closed or shows a move. */
  pickedSignInApp: EamSignInApp | null;
}

export default function EamSteps({
  realm, entra, consentUrl, note, onDismissNote, busy, onSaveOwnApp, onChangeId, users, onSaveMethodId, readAccess, pendingSource,
  pickedSignInApp,
}: Props) {
  const copy = stepCopy(realm.phone_source);
  const parts = step1Parts(realm, consentUrl);
  const appView = signInAppView(realm);
  const ownApp = appView === "register" || appView === "registered";
  // During a move to Mideye's app, the read-access tag sits with Mideye's app, in the consent part.
  const status = parts.mideyeApp
    ? consentStatus(readAccess, realm.phone_source, pendingSource, false)
    : ownApp
      ? consentStatus(readAccess, realm.phone_source, pendingSource, true)
      : null;
  const readStatus = status && <ReadAccessStatus view={status} />;
  const ownAppReadStatus = parts.mideyeApp ? null : readStatus;
  const leftover = mideyeAppLeftover(readAccess, realm.phone_source, pendingSource, ownApp, signInChanging(realm, pickedSignInApp));
  const shownNote = step1Note(note, leftover, pickedSignInApp);
  const [showSteps, setShowSteps] = useState(false);
  const labels = stepsCopy.rowLabels;
  const rows: [string, string, boolean][] = [
    [labels.name, entra.name, true],
    [labels.clientId, stepTwoValue(entra.client_id, realm), !!entra.client_id],
    [labels.discovery, entra.discovery_endpoint, true],
    [labels.appId, stepTwoValue(entra.app_id, realm), !!entra.app_id],
  ];
  const wide = parts.ownApp !== null;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className={`${CARD} ${wide ? "lg:col-span-3" : ""}`}>
        {shownNote && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-md bg-amber-50 p-3">
            <p className="text-sm text-amber-800">{shownNote}</p>
            <button className="text-sm font-medium text-amber-700 hover:text-amber-600" onClick={onDismissNote}>
              {DISMISS_LABEL}
            </button>
          </div>
        )}
        {parts.unknown && (
          <>
            <h3 className="text-base font-semibold text-gray-900">{stepsCopy.unknownTitle}</h3>
            <p className="mt-2 text-sm text-gray-500">{SIGN_IN_APP_NOT_KNOWN_STEP}</p>
          </>
        )}
        {parts.ownApp === "register" && (
          <>
            <div className="flex items-center gap-1">
              <h3 className="text-base font-semibold text-gray-900">{ownAppCopy.registerTitle}</h3>
              <ModeInfoTip id="portal-customer" />
            </div>
            <div className="mt-2">
              <OwnAppSteps
                redirectUri={entra.redirect_uri}
                tenantId={realm.tenant_id}
                phoneSource={realm.phone_source}
                onSave={onSaveOwnApp}
                busy={busy}
                readStatus={ownAppReadStatus}
              />
            </div>
          </>
        )}
        {parts.ownApp === "registered" && (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <h3 className="text-base font-semibold text-gray-900">
                {stepsCopy.registeredPrefix} <span className="break-all font-mono">{realm.sign_in_app?.app_id}</span>
              </h3>
              <button className={LINK} onClick={() => setShowSteps((s) => !s)}>
                {showSteps ? stepsCopy.hideSteps : stepsCopy.showSteps}
              </button>
              {canChangeAppId(realm) && (
                <button className={LINK} onClick={onChangeId}>
                  {stepsCopy.changeId}
                </button>
              )}
            </div>
            {showSteps ? (
              <div className="mt-3">
                <OwnAppSteps
                  redirectUri={entra.redirect_uri}
                  tenantId={realm.tenant_id}
                  phoneSource={realm.phone_source}
                  onSave={onSaveOwnApp}
                  busy={busy}
                  showField={false}
                  readStatus={ownAppReadStatus}
                />
              </div>
            ) : (
              ownAppReadStatus && (
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{ownAppRegisterLine(realm.phone_source)}</p>
                  {ownAppReadStatus}
                </div>
              )
            )}
          </>
        )}
        {leftover && <p className="mt-2 text-sm text-gray-500">{leftover}</p>}
        {parts.consent && (
          <div className={parts.ownApp || parts.unknown ? "mt-5 border-t border-gray-100 pt-4" : ""}>
            {(!parts.ownApp || parts.mideyeApp) && (
              <>
                <div className="flex items-center gap-1">
                  <h3 className="text-base font-semibold text-gray-900">
                    {parts.unknown || parts.ownApp ? withoutStepNumber(copy.consentTitle) : copy.consentTitle}
                  </h3>
                  {parts.mideyeApp && (
                    <InfoTip label={CONSENT_INFO_LABEL}>
                      <p>
                        <b>{CONSENT_INFO.signInOnlyLabel}</b>
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        {CONSENT_INFO.signInOnly.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                      <p className="mt-2">
                        <b>{CONSENT_INFO.readAccessLabel}</b>
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        {CONSENT_INFO.readAccess.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                      <p className="mt-2">{CONSENT_INFO.adminConsent}</p>
                      <p className="mt-2">
                        <a href={CONSENT_INFO.removeUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-accent hover:text-accent-dark">
                          {CONSENT_INFO.removeLink}
                        </a>
                      </p>
                      <p className="mt-1">{CONSENT_INFO.removePath}</p>
                    </InfoTip>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-500">{parts.ownApp && parts.mideyeApp ? MOVE_TO_MIDEYE_CONSENT_BODY : copy.consentBody}</p>
                {readStatus}
              </>
            )}
            <a href={consentUrl ?? undefined} target="_blank" rel="noopener noreferrer" className={`${PRIMARY_BUTTON} mt-4`}>
              {stepsCopy.grantConsent}
            </a>
          </div>
        )}
      </div>

      <div className={`${CARD} ${wide ? "lg:col-span-3" : "lg:col-span-2"}`}>
        <h3 className="text-base font-semibold text-gray-900">{stepsCopy.step2Title}</h3>
        <p className="mt-2 text-sm text-gray-500">
          <RichText parts={stepsCopy.step2Intro} />
        </p>
        {moveView(realm) && <p className="mt-2 text-sm text-gray-700">{MOVE_STEP_TWO_LINE}</p>}
        <dl className="mt-4 divide-y divide-gray-100 text-sm">
          {rows.map(([label, value, copyable]) => (
            <div key={label} className="flex items-center gap-3 py-2.5">
              <dt className="w-40 flex-shrink-0 text-gray-500">{label}</dt>
              <dd className="min-w-0 flex-1 break-all font-mono text-gray-800">{value}</dd>
              {copyable && <CopyButton text={value} title={stepsCopy.copyTitle(label)} />}
            </div>
          ))}
        </dl>
      </div>

      <ListStep title={stepsCopy.step3Title} intro={copy.turnOnBody} items={[...stepsCopy.step3Items, ...copy.numberItems]} />
      <ListStep title={stepsCopy.step4Title} items={stepsCopy.step4Items} />
      <ListStep title={stepsCopy.step5Title} items={stepsCopy.step5Items} />
      <ListStep title={stepsCopy.step6Title} intro={stepsCopy.step6Intro} items={stepsCopy.step6Items}>
        <EamRegisterPanel key={entra.method_id ?? ""} realm={realm} entra={entra} users={users} busy={busy} onSaveMethodId={onSaveMethodId} />
      </ListStep>
    </div>
  );
}

function ListStep({
  title, intro, items, children,
}: { title: string; intro?: string; items: readonly Rich[]; children?: ReactNode }) {
  return (
    <div className={`${CARD} lg:col-span-3`}>
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {intro && <p className="mt-2 text-sm text-gray-500">{intro}</p>}
      {children}
      <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-gray-500">
        {items.map((item, i) => (
          <li key={i}>
            <RichText parts={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}
